import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Popover,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { notificationsService, type AppNotification } from '../../services/notifications.service';
import { useNotificationStream } from '../../hooks/useNotificationStream';
import { useNotification } from '../../notifications/NotificationProvider';
import { NotificationItem } from './NotificationItem';
import { UNREAD_MESSAGES_KEY } from './MessagesBell';

/** Every notification query key starts with this — invalidating it
 *  refreshes both the badge count and the dropdown list at once. */
const NOTIF_KEY = ['notifications'];

/**
 * Header notification bell: an unread-count badge plus a dropdown of the
 * most recent notifications. The count is kept live by the SSE stream and,
 * as a safety net, re-polled every minute.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const unread = useQuery({
    queryKey: [...NOTIF_KEY, 'unread-count'],
    queryFn: notificationsService.unreadCount,
    refetchInterval: 60_000,
  });

  const list = useQuery({
    queryKey: [...NOTIF_KEY, 'list', 'bell'],
    queryFn: () => notificationsService.list({ size: 8 }),
    enabled: open,
  });

  // Live stream — refresh the badge + dropdown and surface a toast. A
  // MESSAGE notification also refreshes the unread-messages badge so both
  // header counts update the instant a message arrives.
  useNotificationStream((n) => {
    qc.invalidateQueries({ queryKey: NOTIF_KEY });
    if (n.type === 'MESSAGE') {
      qc.invalidateQueries({ queryKey: UNREAD_MESSAGES_KEY });
    }
    notify.info(n.body);
  });

  const markAll = useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });

  const markOne = useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIF_KEY }),
  });

  const openNotification = (n: AppNotification) => {
    if (!n.read) markOne.mutate(n.id);
    setAnchor(null);
    // Deep-link by reference; default to the full notifications page.
    if (n.referenceType === 'CONVERSATION') navigate('/messages');
    else navigate('/notifications');
  };

  const count = unread.data ?? 0;
  const items = list.data?.data ?? [];

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ ml: 0.5 }}
        aria-label={t('notifications.title')}
      >
        <Badge badgeContent={count} color="error" max={99}>
          <NotificationsNoneRoundedIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: '100vw', mt: 0.5 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            {t('notifications.title')}
          </Typography>
          {count > 0 && (
            <Button size="small" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Box>
        <Divider />

        {list.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
            {t('notifications.empty')}
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.map((n) => (
              <NotificationItem key={n.id} notification={n} onClick={openNotification} />
            ))}
          </List>
        )}

        <Divider />
        <Button
          fullWidth
          sx={{ py: 1.25, borderRadius: 0 }}
          onClick={() => {
            setAnchor(null);
            navigate('/notifications');
          }}
        >
          {t('notifications.seeAll')}
        </Button>
      </Popover>
    </>
  );
}
